import { useState } from 'react'
import { useSession } from '@/entities/session'
import { dateInputToDay } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { Checkbox } from '@/shared/ui/checkbox'
import { Dialog } from '@/shared/ui/dialog'
import { Field, Input } from '@/shared/ui/field'
import { Select } from '@/shared/ui/select'
import { toast } from '@/shared/ui/toast'
import { dayFieldHint } from '@/shared/ui/zone-badge'
import { useCreateTask } from '../api/create'

interface CreateTaskDialogProps {
  /** Проект задан контекстом (таб задач проекта)… */
  projectId?: string
  /** …или выбирается в диалоге (хаб заказчика): список доступных сделок. */
  projects?: Array<{ id: string; title: string }>
  open: boolean
  onClose: () => void
}

export function CreateTaskDialog({ projectId, projects, open, onClose }: CreateTaskDialogProps) {
  const { session } = useSession()
  const isCustomer = session?.kind === 'customer'
  const [projectChoice, setProjectChoice] = useState('')
  const effectiveProjectId = projectId ?? projectChoice
  const create = useCreateTask(effectiveProjectId)
  const [title, setTitle] = useState('')
  const [due, setDue] = useState('')
  const [blocking, setBlocking] = useState(false)
  const [error, setError] = useState('')
  const [projectError, setProjectError] = useState('')

  function submit() {
    if (!effectiveProjectId) return setProjectError('Выберите проект')
    setProjectError('')
    if (!title.trim()) return setError('Укажите задачу')
    setError('')
    // Срок задачи — календарный день "YYYY-MM-DD": ровно то, что отдаёт
    // <input type="date">.
    const dueDay = dateInputToDay(due)
    create.mutate(
      {
        title: title.trim(),
        ...(dueDay ? { due: dueDay } : {}),
        // blocking ставит только сотрудник.
        ...(isCustomer ? {} : { blocking }),
      },
      {
        onSuccess: () => {
          toast.success('Задача создана')
          onClose()
        },
        onError: () => toast.error('Не удалось создать задачу'),
      },
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Новая задача"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={create.isPending}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={create.isPending}>
            {create.isPending ? 'Создаём…' : 'Создать'}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {projectId == null && (
          <Field label="Проект" {...(projectError ? { error: projectError } : {})}>
            {(props) => (
              <Select
                {...props}
                value={projectChoice}
                onChange={setProjectChoice}
                options={(projects ?? []).map((d) => ({ value: d.id, label: d.title }))}
                placeholder="Выберите проект…"
              />
            )}
          </Field>
        )}
        <Field label="Задача" {...(error ? { error } : {})}>
          {(props) => (
            <Input
              {...props}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Подготовить договор"
            />
          )}
        </Field>
        {/* Срок задачи — день: показываем предпросмотр того, что получится,
            и прямо говорим, что времени у него нет. Зоной день не подписываем. */}
        <Field label="Срок" hint={dayFieldHint(due)}>
          {(props) => (
            <Input {...props} type="date" value={due} onChange={(e) => setDue(e.target.value)} />
          )}
        </Field>
        {!isCustomer && (
          <Checkbox
            checked={blocking}
            onChange={(e) => setBlocking(e.target.checked)}
            label="Блокирующая — держит проект на текущем этапе"
            labelClassName="text-sm text-text-secondary"
          />
        )}
      </div>
    </Dialog>
  )
}
